/**
 * dnsExport — serialise a DNS zone to a BIND zone file or CSV.
 *
 * Pure client-side: the records are already in the browser from `useDnsZone`,
 * so export needs no Edge Function, no migration and no extra API round-trip.
 * It can only ever emit records the user is already looking at on screen.
 *
 * Two formats, two different jobs:
 *   • BIND zone file — the portable one. Take a backup before editing, or hand
 *     the zone to another DNS provider. Importable by virtually every host.
 *   • CSV — the spreadsheet one. Records, audit, sharing with someone who does
 *     not read zone files.
 */
import { toCsv, downloadCsv, todayStamp } from '@/shared/utils/csvExport';
import type { DnsZoneRecord } from '../types/dns.types';

/** RFC 1035 §3.3.14 — a single character-string in a TXT record maxes out at 255 octets. */
const TXT_CHUNK = 255;

/**
 * Keep only lines that are actually resource records.
 *
 * WHM's `dumpzone` does not return a clean RR list — it returns every LINE of
 * the zone file, including pseudo-types for things that are not records at all:
 *
 *   ':RAW'  — a raw/unparsed line (comments, blank lines)
 *   '$TTL'  — a directive
 *   'SOA'   — real, but deliberately excluded from the export (see buildZoneFile)
 *
 * `parseRawRecord` in _shared/whmClient.ts passes these through with an empty
 * `value`, so without this filter the export emitted lines like
 * `@  14400 IN  :RAW` — which Cloudflare rejects with
 * "dns: unknown RR type: ':RAW'". Reported from a real import 2026-07-26.
 *
 * Rule rather than allowlist: drop the WHM pseudo-types (leading ':' or '$'),
 * drop SOA, and drop anything with no value to serialise. That keeps any
 * legitimate RR type WHM might return in future without needing a code change.
 */
export function isExportableRecord(r: DnsZoneRecord): boolean {
  const t = (r.type ?? '').trim().toUpperCase();
  if (t === '' || t.startsWith(':') || t.startsWith('$')) return false;
  if (t === 'SOA') return false;
  return String(r.value ?? '').trim() !== '';
}

/**
 * Quote a TXT/CAA value, splitting anything over 255 chars into adjacent quoted
 * strings the way BIND requires.
 *
 * This is not hypothetical: a 2048-bit DKIM public key is ~400 characters, and
 * emitting it as one quoted string produces a zone file that named-checkzone
 * (and most provider importers) will reject.
 */
function quoteTxt(raw: string): string {
  // Already quoted by WHM? Strip the outer quotes and re-chunk ourselves so the
  // 255-limit is applied consistently.
  let v = raw.trim();
  if (v.startsWith('"') && v.endsWith('"') && v.length >= 2) {
    v = v.slice(1, -1);
  }
  const escaped = v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  if (escaped.length <= TXT_CHUNK) return `"${escaped}"`;

  const parts: string[] = [];
  for (let i = 0; i < escaped.length; i += TXT_CHUNK) {
    parts.push(`"${escaped.slice(i, i + TXT_CHUNK)}"`);
  }
  return parts.join(' ');
}

/**
 * Render the owner name relative to $ORIGIN.
 * WHM's dumpzone returns fully-qualified names with a trailing dot; the apex
 * comes back as "domain.". Emitting "@" for the apex keeps the file readable
 * and portable.
 */
function ownerName(name: string, domain: string): string {
  const n = (name ?? '').trim();
  if (n === '' || n === '@') return '@';
  const bare = n.replace(/\.$/, '').toLowerCase();
  const dom = domain.replace(/\.$/, '').toLowerCase();
  if (bare === dom) return '@';
  if (bare.endsWith(`.${dom}`)) return bare.slice(0, -(dom.length + 1));
  // Not under this origin (shouldn't happen) — keep it fully qualified so the
  // file stays correct rather than silently re-homing the record.
  return n.endsWith('.') ? n : `${n}.`;
}

/**
 * Fully qualify a hostname-valued RDATA field.
 *
 * ⚠ This is the single most dangerous detail in the whole exporter.
 *
 * WHM's dumpzone returns target hostnames WITHOUT a trailing dot — e.g.
 * `mx1.tld-mx.com`, `ns1.cpns.host`, `nobulindi.co.za`. In a zone file with an
 * $ORIGIN, a name that does not end in '.' is RELATIVE and the origin is
 * appended. Emitting WHM's value verbatim therefore produced:
 *
 *     @  14400 IN MX 10 mx1.tld-mx.com        ->  mx1.tld-mx.com.nobulindi.co.za.
 *     www       IN CNAME nobulindi.co.za      ->  nobulindi.co.za.nobulindi.co.za.
 *     @         IN NS  ns1.cpns.host          ->  ns1.cpns.host.nobulindi.co.za.
 *
 * Importing that would have silently broken MAIL DELIVERY and delegation.
 * Caught on a real nobulindi.co.za export before import, 2026-07-26.
 *
 * Rule: if the value contains a dot and is not already absolute, make it
 * absolute. A bare single label (no dots) is left alone — that is a legitimate
 * relative reference to a host inside this zone.
 */
function fqdn(value: string): string {
  const v = (value ?? '').trim();
  if (v === '' || v.endsWith('.')) return v;
  return v.includes('.') ? `${v}.` : v;
}

/** One record → one zone-file line. */
function recordLine(r: DnsZoneRecord, domain: string): string {
  const owner = ownerName(r.name, domain).padEnd(20);
  const ttl = String(r.ttl ?? 14400).padStart(6);
  const type = (r.type ?? '').toUpperCase();
  const head = `${owner} ${ttl} IN  ${type.padEnd(5)}`;

  switch (type) {
    case 'MX':
      return `${head} ${r.priority ?? 0} ${fqdn(r.value)}`;
    case 'SRV':
      return `${head} ${r.priority ?? 0} ${r.weight ?? 0} ${r.port ?? 0} ${fqdn(r.value)}`;
    case 'CNAME':
    case 'NS':
    case 'PTR':
    case 'DNAME':
      return `${head} ${fqdn(r.value)}`;
    case 'CAA':
      return `${head} ${r.flag ?? 0} ${r.tag ?? 'issue'} ${quoteTxt(r.value)}`;
    case 'TXT':
      return `${head} ${quoteTxt(r.value)}`;
    default:
      // A / AAAA and anything else: the value is an address or opaque data,
      // NOT a hostname — must not be dot-qualified.
      return `${head} ${r.value}`;
  }
}

/**
 * Build a BIND-format zone file.
 *
 * SOA is deliberately omitted. The receiving provider always generates its own
 * (serial, refresh timers and their own primary NS), and several importers
 * reject a file that carries one. The header comment says so, so nobody thinks
 * the export was truncated.
 */
export function buildZoneFile(domain: string, records: DnsZoneRecord[]): string {
  const dom = domain.replace(/\.$/, '');
  // Drop WHM pseudo-types (':RAW', '$TTL', …) and SOA — see isExportableRecord.
  const exportable = records.filter(isExportableRecord);
  const skipped = records.length - exportable.length;

  const lines: string[] = [
    `; Zone file for ${dom}`,
    `; Exported from SightHost (${todayStamp()})`,
    ';',
    '; SOA is intentionally not included — your DNS provider generates its own',
    '; on import. Review NS records before importing elsewhere.',
  ];
  if (skipped > 0) {
    // Say so rather than silently shrinking the file — a record count that does
    // not match the UI would otherwise look like data loss.
    lines.push(
      ';',
      `; ${skipped} non-record line${skipped === 1 ? '' : 's'} omitted (SOA / comments /`,
      '; directives). These are not resource records and are regenerated by the',
      '; receiving provider.',
    );
  }
  lines.push(';', `$ORIGIN ${dom}.`, '$TTL 14400', '');

  // Group by type so the file reads the way an operator expects.
  const order = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA'];
  const sorted = [...exportable].sort((a, b) => {
    const ai = order.indexOf((a.type ?? '').toUpperCase());
    const bi = order.indexOf((b.type ?? '').toUpperCase());
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return ownerName(a.name, dom).localeCompare(ownerName(b.name, dom));
  });

  let lastType = '';
  for (const r of sorted) {
    const t = (r.type ?? '').toUpperCase();
    if (t !== lastType) {
      if (lastType !== '') lines.push('');
      lines.push(`; ${t} records`);
      lastType = t;
    }
    lines.push(recordLine(r, dom));
  }

  lines.push('');
  return lines.join('\n');
}

/** Trigger a browser download of the zone file. */
export function downloadZoneFile(domain: string, records: DnsZoneRecord[]): void {
  const dom = domain.replace(/\.$/, '');
  const blob = new Blob([buildZoneFile(dom, records)], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${dom}-${todayStamp()}.zone`;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger a browser download of the zone as CSV.
 * Reuses the shared csvExport helper (RFC 4180 quoting + Excel BOM) rather than
 * hand-rolling a second serialiser — ARCHITECTURE_FRAMEWORK §4.2.
 */
export function downloadZoneCsv(domain: string, records: DnsZoneRecord[]): void {
  const dom = domain.replace(/\.$/, '');
  const header = ['Name', 'Type', 'TTL', 'Value', 'Priority', 'Weight', 'Port', 'Flag', 'Tag'];
  // Same filter as the zone file — a ':RAW' line is not a DNS record.
  const rows = records.filter(isExportableRecord).map((r) => [
    ownerName(r.name, dom),
    (r.type ?? '').toUpperCase(),
    r.ttl ?? '',
    r.value ?? '',
    r.priority ?? '',
    r.weight ?? '',
    r.port ?? '',
    r.flag ?? '',
    r.tag ?? '',
  ]);
  downloadCsv(`${dom}-dns-${todayStamp()}.csv`, toCsv([header, ...rows]));
}
